import React, { useState, useRef, useEffect } from 'react';
import { FaTimes, FaChevronLeft, FaChevronRight, FaExpand, FaCompress, FaImage, FaVideo } from 'react-icons/fa';
import { getGuestDisplayName } from '../../utils/guestDisplay';
import './ImageGallery.css';

const ImageGallery = ({ content, isOpen, onClose, initialIndex = 0 }) => {
  const [currentIndex, setCurrentIndex] = useState(initialIndex);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [touchStart, setTouchStart] = useState(0);
  const [touchEnd, setTouchEnd] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  const carouselRef = useRef(null);

  useEffect(() => {
    if (isOpen) {
      setCurrentIndex(initialIndex);
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'auto';
    }
    return () => {
      document.body.style.overflow = 'auto';
    };
  }, [isOpen, initialIndex]);

  const handleNext = () => {
    if (isAnimating) return;
    setIsAnimating(true);
    setCurrentIndex((prev) => (prev + 1) % content.length);
    setTimeout(() => setIsAnimating(false), 300);
  };

  const handlePrev = () => {
    if (isAnimating) return;
    setIsAnimating(true);
    setCurrentIndex((prev) => (prev - 1 + content.length) % content.length);
    setTimeout(() => setIsAnimating(false), 300);
  };

  const handleTouchStart = (e) => {
    setTouchStart(e.targetTouches[0].clientX);
  };

  const handleTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const handleTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > 50;
    const isRightSwipe = distance < -50;

    if (isLeftSwipe) {
      handleNext();
    }
    if (isRightSwipe) {
      handlePrev();
    }

    setTouchStart(0);
    setTouchEnd(0);
  };

  const toggleFullscreen = () => {
    setIsFullscreen(!isFullscreen);
  };

  const goToSlide = (index) => {
    if (isAnimating) return;
    setIsAnimating(true);
    setCurrentIndex(index);
    setTimeout(() => setIsAnimating(false), 300);
  };

  if (!isOpen || content.length === 0) return null;

  const currentItem = content[currentIndex];

  return (
    <div className={`image-gallery-overlay ${isFullscreen ? 'fullscreen' : ''}`}>
      <div className="gallery-header">
        <button className="gallery-close-btn" onClick={onClose}>
          <FaTimes />
        </button>
        <div className="gallery-counter">
          {currentIndex + 1} / {content.length}
        </div>
        <button className="gallery-fullscreen-btn" onClick={toggleFullscreen}>
          {isFullscreen ? <FaCompress /> : <FaExpand />}
        </button>
      </div>

      <div 
        className="gallery-main-content"
        ref={carouselRef}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        {content.length > 1 && (
          <button 
            className="gallery-nav-btn prev-btn" 
            onClick={handlePrev}
            disabled={isAnimating}
          >
            <FaChevronRight />
          </button>
        )}

        <div className={`gallery-slide ${isAnimating ? 'animating' : ''}`}>
          {currentItem.type === 'image' ? (
            <img 
              src={currentItem.url} 
              alt={currentItem.name || 'صورة'} 
              className="gallery-media"
            />
          ) : (
            <video 
              src={currentItem.url} 
              controls 
              className="gallery-media"
              autoPlay={false}
            />
          )}
          <div className="gallery-media-info">
            {currentItem.type === 'image' ? <FaImage /> : <FaVideo />}
            <span>{currentItem.name || 'محتوى'}</span>
            <span style={{ opacity: 0.8, fontSize: '0.9em' }}>
              {getGuestDisplayName(currentItem.guestName, currentItem.guestPhone)}
            </span>
          </div>
        </div>

        {content.length > 1 && (
          <button 
            className="gallery-nav-btn next-btn" 
            onClick={handleNext}
            disabled={isAnimating}
          >
            <FaChevronLeft />
          </button>
        )}
      </div>

      {content.length > 1 && (
        <div className="gallery-thumbnail-strip">
          <div className="thumbnail-container">
            {content.map((item, index) => (
              <div
                key={item.id}
                className={`thumbnail-item ${index === currentIndex ? 'active' : ''}`}
                onClick={() => goToSlide(index)}
              >
          {item.type === 'image' ? (
            <img src={item.thumbUrl || item.url} alt={item.name || 'thumbnail'} />
          ) : (
            <div className="thumbnail-video">
              <video src={item.thumbUrl || item.url} />
              <FaVideo className="thumbnail-video-icon" />
            </div>
          )}
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="gallery-dots">
        {content.map((_, index) => (
          <span
            key={index}
            className={`dot ${index === currentIndex ? 'active' : ''}`}
            onClick={() => goToSlide(index)}
          />
        ))}
      </div>
    </div>
  );
};

export default ImageGallery;
